import { Role } from 'auth';
import { useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import { useCurrentUserRegistersQuery } from '@/modules/investor-dashboard/queries/registers.tsx';
import { IssuerDashboardWrapperOutletContext } from '@/modules/issuer-dashboard/components/IssuerDashboardWrapper.tsx';
import IssuerTaxReportsRegisterItem from '@/modules/issuer-dashboard/components/items/IssuerTaxReportsRegisterItem.tsx';
import { getTaxReportYearOptions } from '@/modules/issuer-dashboard/helpers/taxReportYears.ts';
import { useRegisterTotalFundByYearListQuery } from '@/modules/issuer-dashboard/queries/tax-reports.ts';
import { EmptyListState, FilterItem, PageLayout, Spinner } from 'ui';
import { useSaveState } from 'ui/hooks';

const IssuerTaxReports = () => {
  const { registerId } = useOutletContext<IssuerDashboardWrapperOutletContext>();
  const yearOptions = useMemo(() => getTaxReportYearOptions(11), []);
  const { isLoading: isLoadingRegisters, data: registersData } = useCurrentUserRegistersQuery();
  const [selectedYear, setSelectedYear] = useSaveState<number>(
    yearOptions[0]?.value ?? new Date().getFullYear(),
    `filters-tax-reports-year-${registerId || 'default'}`,
  );
  const { isLoading: isLoadingTotalFund, data: totalFundData } =
    useRegisterTotalFundByYearListQuery(selectedYear);

  const totalFundByRegisterId = useMemo(
    () => new Map((totalFundData?.registers || []).map(item => [item.register_id, item])),
    [totalFundData?.registers],
  );

  const allowedRegisters = useMemo(
    () =>
      (registersData?.data || []).filter(
        register => !!register.register_obj && totalFundByRegisterId.has(register.register_id),
      ),
    [registersData?.data, totalFundByRegisterId],
  );
  const displayRegisters = useMemo(
    () =>
      allowedRegisters.flatMap(register => {
        if (!register.register_obj) return [];
        return [
          {
            registerId: register.register_id,
            registerObj: register.register_obj,
            totalFund: totalFundByRegisterId.get(register.register_id)?.total_fund,
          },
        ];
      }),
    [allowedRegisters, totalFundByRegisterId],
  );

  const isLoading = isLoadingRegisters || isLoadingTotalFund;

  return (
    <PageLayout
      title="Tax reports"
      headerAction={
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-900">Year</span>
          <FilterItem
            type="select"
            className="h-10 w-[220px] rounded-xl border-[rgba(23,23,23,0.12)]"
            placeholder="Year"
            isClearable={false}
            value={selectedYear}
            options={yearOptions}
            onChange={year => {
              if (year !== null) {
                setSelectedYear(year);
              }
            }}
            classes={{
              element: '!py-0 flex items-center w-full',
            }}
          />
        </div>
      }
      classNameTitle="flex-row justify-between items-center"
    >
      {isLoading && (
        <div className="h-full w-full flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      )}
      <EmptyListState
        title="No registers"
        description="You have not been added to any registry."
        isLoading={isLoading}
        isSearchUsed={false}
        itemsCount={displayRegisters.length}
      />
      {!isLoading && !!displayRegisters.length && (
        <div className="flex flex-col gap-2">
          {displayRegisters.map(item => {
            return (
              <IssuerTaxReportsRegisterItem
                register={item.registerObj}
                selectedYear={selectedYear}
                totalFund={item.totalFund}
                key={item.registerId}
              />
            );
          })}
        </div>
      )}
    </PageLayout>
  );
};

export default withPrivateAccess(IssuerTaxReports, [Role.SR_ISSUER, Role.SR_ISSUER_ADMIN]);
